import { AlertTriangle, ArrowUp, Cloud, Gauge, Navigation, Wind } from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  ForecastHour,
  ForecastResponse,
  HeightFrame,
  UnitProfile
} from "../types";
import {
  formatHeight,
  formatSecondaryFpm,
  formatSpeed,
  formatTemperature,
  heightUnitLabel,
  speedUnitLabel,
  temperatureUnitLabel
} from "../units/conversions";
import { compass } from "../windgram/physics";

function scalar(value: number | null | undefined, digits = 0, locale?: string): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits, useGrouping: false }).format(value);
}

function height(
  hour: ForecastHour,
  frame: HeightFrame,
  kind: "thermal" | "usable" | "cloudbase",
  forecast: ForecastResponse
): number | null {
  if (kind === "thermal") {
    if (frame === "altitude_msl_ft") return hour.thermal_top_msl_ft;
    if (frame === "altitude_model_agl_ft") return hour.thermal_top_model_agl_ft;
    return hour.thermal_top_launch_agl_ft;
  }
  if (kind === "usable") {
    if (frame === "altitude_msl_ft") return hour.usable_top_msl_ft;
    if (frame === "altitude_model_agl_ft") return hour.usable_top_model_agl_ft;
    return hour.usable_top_launch_agl_ft;
  }
  if (hour.cloudbase_msl_ft === null) return null;
  if (frame === "altitude_msl_ft") return hour.cloudbase_msl_ft;
  if (frame === "altitude_model_agl_ft") {
    return hour.cloudbase_msl_ft - forecast.source.model_terrain_ft;
  }
  return hour.cloudbase_launch_agl_ft;
}

export function HourInspector({
  hour,
  forecast,
  frame,
  units
}: {
  hour: ForecastHour;
  forecast: ForecastResponse;
  frame: HeightFrame;
  units: UnitProfile;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const character = hour.thermal_character;
  const stress = character.strong_core;
  const usable = height(hour, frame, "usable", forecast);
  const thermal = height(hour, frame, "thermal", forecast);
  const cloudbase = height(hour, frame, "cloudbase", forecast);
  const ppg = hour.paramotor;
  const heightUnit = heightUnitLabel(units, frame);
  const windUnit = speedUnitLabel(units);
  const reasonLabel = (reason: string | null | undefined) => {
    const key = `models.reason.${reason ?? "unknown"}`;
    const value = t(key);
    return value === key ? t("models.reason.unknown") : value;
  };

  return (
    <aside className="hour-inspector" aria-live="polite">
      <div className="inspector-heading">
        <div>
          <span className="eyebrow">{t("inspector.selectedHour")}</span>
          <h2>{String(hour.hour).padStart(2, "0")}:00</h2>
        </div>
        <span className={`phase-pill phase-${character.phase.state}`}>
          {t(`inspector.state.${character.phase.state}`)}
        </span>
      </div>

      <div className="headline-metrics">
        <div>
          <ArrowUp size={16} />
          <span>W*</span>
          <strong>
            {hour.w_star_ms === null ? "—" : `${scalar(hour.w_star_ms, 1, locale)} m/s`}
          </strong>
          <small>
            {hour.w_star_ms === null
              ? `${t("inspector.convectiveStrength")} ${t("common.unavailable").toLowerCase()}`
              : `${formatSecondaryFpm(hour.w_star_ms, locale)} fpm`}
          </small>
        </div>
        <div>
          <Gauge size={16} />
          <span>{t("charts.usableTop")}</span>
          <strong>{formatHeight(usable, units, 0, locale)}</strong>
          <small>{heightUnit}</small>
        </div>
        <div>
          <Wind size={16} />
          <span>{t("inspector.surface")}</span>
          <strong>{formatSpeed(hour.surface_wind_kt, units, 1, locale)} {windUnit}</strong>
          <small>
            {t("inspector.gustValue", {
              direction: compass(hour.surface_wind_dir),
              speed: formatSpeed(hour.wind_gust_kt, units, 0, locale),
              unit: windUnit
            })}
          </small>
        </div>
      </div>

      <section className="inspector-section">
        <div className="section-label">{t("charts.character")}</div>
        <div className="axis-grid">
          {(
            [
              ["power", character.power],
              ["texture", character.texture],
              ["organization", character.organization],
              ["phase", character.phase]
            ] as const
          ).map(([label, axis]) => (
            <div className={`axis-card state-${axis.state}`} key={label}>
              <span>{t(`inspector.axis.${label}`)}</span>
              <strong>{t(`inspector.state.${axis.state}`)}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="stress-card">
        <div className="stress-heading">
          <AlertTriangle size={17} />
          <span>{t("inspector.stressTitle")}</span>
          <small>{t("inspector.uncalibrated")}</small>
        </div>
        <strong>
          {stress.available && stress.air_climb_low_ms !== null
            ? `${scalar(stress.air_climb_low_ms, 1, locale)}–${scalar(stress.air_climb_high_ms, 1, locale)} m/s`
            : character.power.state === "unknown"
              ? t("common.unavailable")
              : t("svg.texture.inactive")}
        </strong>
        <p>
          {t("inspector.stressBody", {
            low: scalar(stress.multiplier_low, 0, locale),
            high: scalar(stress.multiplier_high, 0, locale)
          })}
        </p>
      </section>

      <section className="inspector-section evidence-section">
        <div className="section-label">{t("inspector.evidence")}</div>
        {hour.availability?.profile && !hour.availability.profile.available ? (
          <p className="availability-note">{t("charts.unavailableReason", { reason: reasonLabel(hour.availability.profile.reason) })}</p>
        ) : null}
        <dl>
          <div>
            <dt>{t("charts.thermalTop")}</dt>
            <dd>
              {thermal === null
                ? "—"
                : `${formatHeight(thermal, units, 0, locale)} ${heightUnit}`}
            </dd>
          </div>
          <div>
            <dt>{t("inspector.netClimb")}</dt>
            <dd>
              {hour.net_climb_ms === null
                ? "—"
                : `${scalar(hour.net_climb_ms, 1, locale)} m/s`}
            </dd>
          </div>
          <div>
            <dt>B / S</dt>
            <dd>
              {hour.buoyancy_shear_ratio === null
                ? "—"
                : scalar(hour.buoyancy_shear_ratio, 1, locale)}
            </dd>
          </div>
          <div>
            <dt>{t("inspector.layerShear")}</dt>
            <dd>{hour.wind_shear_ms === null ? "—" : `${scalar(hour.wind_shear_ms, 1, locale)} m/s`}</dd>
          </div>
          <div>
            <dt>{t("inspector.blWind")}</dt>
            <dd>{formatSpeed(hour.mixed_layer_wind_kt, units, 1, locale)} {windUnit}</dd>
          </div>
          <div>
            <dt>{t("inspector.gustSpread")}</dt>
            <dd>
              {hour.wind_gust_kt === null || hour.surface_wind_kt === null
                ? "—"
                : `${formatSpeed(hour.wind_gust_kt - hour.surface_wind_kt, units, 1, locale)} ${windUnit}`}
            </dd>
          </div>
          <div>
            <dt>{t("inspector.heatFlux")}</dt>
            <dd>{scalar(hour.sensible_heat_flux_wm2, 0, locale)} W/m²</dd>
          </div>
          <div>
            <dt>{t("inspector.triggerTemperature")}</dt>
            <dd>
              {formatTemperature(hour.trigger_temperature_f, units, 0, locale)}{temperatureUnitLabel(units)}
            </dd>
          </div>
          <div>
            <dt>{t("charts.cloudbase")}</dt>
            <dd className="cloud-evidence">
              <Cloud size={14} />{" "}
              {cloudbase === null ? "—" : `${formatHeight(cloudbase, units, 0, locale)} ${heightUnit}`}
            </dd>
          </div>
        </dl>
      </section>

      {ppg && (
        <section className="inspector-section ppg-section">
          <div className="section-label">
            <Navigation size={14} /> {t("controls.paramotor")}
          </div>
          <dl>
            <div>
              <dt>{t("inspector.densityAltitude")}</dt>
              <dd>
                {ppg.density
                  ? `${formatHeight(ppg.density.density_altitude_ft, units, 0, locale)} ${heightUnitLabel(units)}`
                  : t("charts.unavailableReason", {
                      reason: reasonLabel(ppg.density_availability?.reason)
                    })}
              </dd>
            </div>
            <div>
              <dt>{t("inspector.surfaceCoupling")}</dt>
              <dd>{t(`svg.couplingState.${ppg.surface_coupling.state}`, { defaultValue: t("common.unknown") })}</dd>
            </div>
            <div>
              <dt>{t("inspector.missionGeometry")}</dt>
              <dd>{ppg.mission.available ? t("inspector.available") : t("inspector.needsConfiguration")}</dd>
            </div>
          </dl>
          <p>
            {t("inspector.ppgCaveat")}
          </p>
        </section>
      )}
    </aside>
  );
}
